import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamsAPI, performanceAPI, usersAPI } from '../services/api';
import { Card, PageHeader, Badge, Button, Loading, Alert, Avatar, Modal, FormField } from '../components/ui';
import { ArrowLeft, UsersRound, Hash, Calendar, FileText, Crown, ChevronRight, Star, StarHalf, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './TeamDetails.css';

const ROLE_COLORS = {
  Admin: '#c084fc',
  Manager: '#06d6a0',
  Member: '#60a5fa',
  Client: '#ffd166',
};

export default function TeamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { canUpdate } = useAuth();
  
  // Edit state
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', code: '', team_lead_id: '', memberIds: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const res = await teamsAPI.get(id);
      const teamData = res.data.data;
      setTeam(teamData);

      // Fetch performance ratings for this team
      try {
        const perfRes = await performanceAPI.getTeamRatings(id);
        const ratingsMap = {};
        perfRes.data.data.forEach(item => {
          ratingsMap[item.user_id] = item.performance;
        });
        setRatings(ratingsMap);
      } catch (perfErr) {
        console.warn('Failed to load performance ratings:', perfErr);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load team details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, [id]);

  const openEditModal = async () => {
    try {
      if (allUsers.length === 0) {
        const res = await usersAPI.list();
        setAllUsers(res.data.data || []);
      }
      setEditForm({
        name: team.name,
        description: team.description || '',
        code: team.code || '',
        team_lead_id: team.team_lead_id || '',
        memberIds: (team.members || []).map(m => m.user_id),
      });
      setEditModal(true);
    } catch (err) {
      setError('Failed to load users for editing.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description,
        code: editForm.code,
        memberIds: editForm.memberIds,
        team_lead_id: editForm.team_lead_id ? Number(editForm.team_lead_id) : null,
      };
      await teamsAPI.update(id, payload);
      setEditModal(false);
      fetchTeam();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update team.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="team-details-page animate-in">
        <Loading message="Loading team details..." />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="team-details-page animate-in">
        <Button variant="ghost" onClick={() => navigate('/app/users')} style={{ marginBottom: 20 }}>
          <ArrowLeft size={16} /> Back to Team list
        </Button>
        <Alert type="error" message={error || 'Team not found'} />
      </div>
    );
  }

  return (
    <div className="team-details-page animate-in">
      <div className="team-details__back">
        <Button variant="ghost" onClick={() => navigate('/app/users')}>
          <ArrowLeft size={16} /> Back
        </Button>
      </div>

      <div className="team-details__header-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div className="team-details__icon">
            <UsersRound size={32} />
          </div>
          <div className="team-details__header-info">
            <h1 className="team-details__title">{team.name}</h1>
            <div className="team-details__badges">
              {team.code && <Badge value={team.code} />}
              <span className="team-details__member-badge">
                {(team.members || []).length} member{(team.members || []).length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        {canUpdate && (
          <Button variant="secondary" onClick={openEditModal}>
            <Pencil size={15} style={{ marginRight: '6px' }} /> Edit Team
          </Button>
        )}
      </div>

      <div className="team-details__content">
        <div className="team-details__main">
          {/* Info Card */}
          <Card className="team-details__section-card">
            <h3 className="team-details__section-title">Team Information</h3>
            <div className="team-details__info-grid">
              {team.code && (
                <div className="team-details__info-item">
                  <Hash size={16} />
                  <div>
                    <label>Code</label>
                    <span>{team.code}</span>
                  </div>
                </div>
              )}
              <div className="team-details__info-item">
                <UsersRound size={16} />
                <div>
                  <label>Total Members</label>
                  <span>{(team.members || []).length}</span>
                </div>
              </div>
              {team.createdAt && (
                <div className="team-details__info-item">
                  <Calendar size={16} />
                  <div>
                    <label>Created</label>
                    <span>
                      {new Date(team.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {team.description && (
              <div className="team-details__desc">
                <FileText size={16} />
                <p>{team.description}</p>
              </div>
            )}
          </Card>

          {/* Members List */}
          <Card className="team-details__section-card">
            <h3 className="team-details__section-title">Members ({(team.members || []).length})</h3>
            {(team.members || []).length === 0 ? (
              <p className="team-details__empty">No members in this team yet.</p>
            ) : (
              <div className="team-details__members-list">
                {(team.members || []).map(m => {
                  const perf = ratings[m.user_id] || { rating: 0, label: 'Unrated' };
                  
                  // Simple logic to render stars
                  const fullStars = Math.floor(perf.rating);
                  const hasHalfStar = perf.rating % 1 >= 0.5;
                  const emptyStars = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));
                  
                  return (
                    <div key={m.user_id} className="team-details__member-row team-details__member-row--clickable" onClick={() => navigate(`/app/users/${m.user_id}`)}>
                      <Avatar
                        name={m.user?.name}
                        size="md"
                        color={ROLE_COLORS[m.user?.role] || 'var(--accent)'}
                        style={{ borderRadius: '50%' }}
                      />
                      <div className="team-details__member-info">
                        <div className="team-details__member-name">{m.user?.name}</div>
                        <div className="team-details__member-meta">
                          {m.user?.title || m.user?.role || '—'}
                          {m.user?.email && ` · ${m.user.email}`}
                        </div>
                        
                        {/* Rating UI */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                          <div style={{ display: 'flex', color: 'var(--accent)', alignItems: 'center' }}>
                            {[...Array(fullStars)].map((_, i) => <Star key={`full-${i}`} size={14} fill="currentColor" />)}
                            {hasHalfStar && <StarHalf size={14} fill="currentColor" />}
                            {[...Array(emptyStars)].map((_, i) => <Star key={`empty-${i}`} size={14} />)}
                            <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{perf.rating.toFixed(1)}</span>
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border-bright)' }}>
                            {perf.label}
                          </span>
                        </div>
                      </div>
                      <Badge value={m.user?.role} />
                      {team.team_lead_id === m.user_id && (
                        <span className="team-details__lead-badge">Lead</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        <div className="team-details__side">
          {/* Manager Card */}
          <Card className="team-details__section-card">
            <h3 className="team-details__section-title">Manager</h3>
            {team.teamLead ? (
              <div className="team-details__lead-card team-details__lead-card--clickable" onClick={() => navigate(`/app/users/${team.team_lead_id}`)}>
                <Avatar
                  name={team.teamLead.name}
                  size="lg"
                  color="var(--accent)"
                  style={{ borderRadius: '50%' }}
                />
                <div className="team-details__lead-info">
                  <div className="team-details__lead-name">{team.teamLead.name}</div>
                  <div className="team-details__lead-meta">
                    {team.teamLead.title || team.teamLead.role}
                  </div>
                  <div className="team-details__lead-email">{team.teamLead.email}</div>
                </div>
                <Crown size={20} className="team-details__crown" />
              </div>
            ) : (
              <p className="team-details__empty">No manager assigned.</p>
            )}
          </Card>
        </div>
      </div>

      {canUpdate && (
        <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit Team" width={600}>
          <form onSubmit={handleEditSubmit}>
            <FormField label="Team name">
              <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
            </FormField>
            <FormField label="Description">
              <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Team code (optional)">
                <input value={editForm.code} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} />
              </FormField>
              <FormField label="Manager (optional)">
                <select value={editForm.team_lead_id} onChange={e => setEditForm(f => ({ ...f, team_lead_id: e.target.value }))}>
                  <option value="">Select Manager...</option>
                  {allUsers.filter(u => u.role === 'Manager').map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.name} ({u.title || u.role})</option>
                  ))}
                </select>
              </FormField>
            </div>
            <FormField label="Members">
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--bg-hover)' }}>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {allUsers.map(u => (
                    <label
                      key={u.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: editForm.memberIds.includes(u.user_id) ? 'rgba(108,99,255,0.12)' : 'transparent',
                      }}
                      onMouseEnter={e => !editForm.memberIds.includes(u.user_id) && (e.currentTarget.style.background = 'var(--bg-card)')}
                      onMouseLeave={e => !editForm.memberIds.includes(u.user_id) && (e.currentTarget.style.background = 'transparent')}
                    >
                      <input
                        type="checkbox"
                        checked={editForm.memberIds.includes(u.user_id)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setEditForm(f => ({
                            ...f,
                            memberIds: checked ? [...f.memberIds, u.user_id] : f.memberIds.filter(id => id !== u.user_id),
                          }));
                        }}
                        style={{ cursor: 'pointer', width: 18, height: 18, accentColor: 'var(--accent)' }}
                      />
                      <Avatar name={u.name} size="sm" color={ROLE_COLORS[u.role] || 'var(--accent)'} style={{ borderRadius: '50%' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{u.title || u.role} &bull; {u.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                {editForm.memberIds.length > 0
                  ? `${editForm.memberIds.length} member${editForm.memberIds.length > 1 ? 's' : ''} selected`
                  : 'Select at least one member'}
              </div>
            </FormField>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="secondary" onClick={() => setEditModal(false)} type="button">Cancel</Button>
              <Button type="submit" disabled={saving || editForm.memberIds.length === 0}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
